import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Card,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { getGroupAdminClinics, type LiteInstance, type Pagination as Paging } from '../../rtk-apis/groupAdmin/groupAdminApi';
import '../studies/cardiacStudyList.css';
import '../reports/cardiacReportList.css';

/* ════════════════════════════════════════════════════════════════════════
   Shared pieces of the Group Admin screens, on the same surface as the
   Studies and Reports lists (cardiacStudyList.css `csl-*`): command bar,
   filter rail, glass row cards, status pill, pager, empty state. Plus the
   table cells the dashboard and report view use, and the clip viewer.
   ════════════════════════════════════════════════════════════════════════ */

// ---- formatting ----------------------------------------------------------------

/** DICOM (YYYYMMDD) or ISO date as DD/MM/YYYY; '—' when absent. */
export const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const m = moment(value, ['YYYYMMDD', 'YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m.format('DD/MM/YYYY') : value;
};

/** "LAST^FIRST" as "LAST, FIRST". */
export const formatName = (value?: string | null) =>
  value ? value.replace(/\^+/g, ', ').replace(/,\s*$/, '') : '(Unknown patient)';

export const initialsOf = (name?: string | null) => {
  const parts = (name || '').replace(/[\^,]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ---- status --------------------------------------------------------------------

const STATUS_COLOR: Record<string, string> = { draft: T.amber, preliminary: T.sky, finalized: T.green };

export const statusColor = (s?: string | null) => STATUS_COLOR[(s || '').toLowerCase()] || T.ink3;

/** Status pill coloured from the theme, so it works inside or outside a `.csl` list. */
export const StatusPill: React.FC<{ status?: string | null; empty?: string }> = ({ status, empty = 'No report' }) =>
  status ? (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: '3px',
        borderRadius: '999px',
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: statusColor(status),
        background: `${statusColor(status)}1f`,
        border: `1px solid ${statusColor(status)}66`,
      }}
    >
      {status}
    </Box>
  ) : (
    <Typography component="span" sx={{ fontSize: 12.5, color: T.ink3 }}>
      {empty}
    </Typography>
  );

// ---- list surface --------------------------------------------------------------

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

/** Title, count, search and tools — the list command bar. */
export const CommandBar: React.FC<{
  title: string;
  count: number;
  noun: string;
  search?: { value: string; placeholder: string; onChange: (v: string) => void };
  tools?: React.ReactNode;
}> = ({ title, count, noun, search, tools }) => (
  <div className="csl-cmd">
    <div className="csl-cmd__title">
      <h1 className="csl-h1">{title}</h1>
      <span className="csl-count">
        <b>{count.toLocaleString()}</b> {noun}
      </span>
    </div>
    <div className="csl-cmd__tools">
      {search && (
        <label className="csl-search">
          <SearchIcon />
          <input type="text" placeholder={search.placeholder} value={search.value} onChange={(e) => search.onChange(e.target.value)} />
        </label>
      )}
      {tools}
    </div>
  </div>
);

/** Segmented single-choice filter ("All" + options). */
export const Segmented: React.FC<{
  value: string;
  options: { value: string; label: string; className?: string }[];
  onChange: (v: string) => void;
}> = ({ value, options, onChange }) => (
  <div className="csl-seg">
    {options.map((o) => (
      <button key={o.value || 'all'} className={`csl-seg__btn ${o.className || ''} ${value === o.value ? 'is-active' : ''}`} onClick={() => onChange(o.value)}>
        {o.label}
      </button>
    ))}
  </div>
);

/**
 * Filter dropdown in the style of the Reports list's label filter (ReportLabelFilter):
 * a pill button that opens a dark menu with check marks. A native <select> renders its
 * popup with OS chrome, which looks wrong on these lists. Searchable past 8 options.
 */
export const FilterMenu: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  /** Shown when nothing is picked, and as the "everything" option. */
  allLabel: string;
  heading: string;
  icon?: React.ReactNode;
}> = ({ value, options, onChange, allLabel, heading, icon }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.value === value);
  const active = !!selected;
  const shown = options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));
  const pick = (v: string) => {
    onChange(v);
    setAnchor(null);
    setQuery('');
  };
  const itemSx = { gap: 1, py: 0.75, fontSize: 13, color: '#e2e8f0' };
  const check = (on: boolean) => <CheckIcon sx={{ fontSize: 15, flexShrink: 0, color: on ? '#5eead4' : 'transparent' }} />;

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={icon}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{
          height: 34,
          px: 1.5,
          maxWidth: 280,
          borderRadius: '9px',
          textTransform: 'none',
          fontSize: 12.5,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          justifyContent: 'flex-start',
          color: active ? '#5eead4' : 'rgba(148,163,184,0.95)',
          border: `1px solid ${active ? 'rgba(45,212,191,0.55)' : 'rgba(148,163,184,0.22)'}`,
          bgcolor: active ? 'rgba(45,212,191,0.10)' : 'rgba(148,163,184,0.06)',
          '&:hover': { bgcolor: active ? 'rgba(45,212,191,0.16)' : 'rgba(148,163,184,0.12)', borderColor: 'rgba(45,212,191,0.45)' },
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? selected.label : allLabel}
        </Box>
      </Button>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        autoFocus={options.length <= 8}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 240,
              maxHeight: 380,
              bgcolor: '#0b1b2b',
              backgroundImage: 'none',
              border: '1px solid rgba(148,163,184,0.22)',
              borderRadius: 2,
              boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
            },
          },
        }}
      >
        <Typography sx={{ px: 1.75, pt: 1, pb: 0.75, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(148,163,184,0.75)' }}>
          {heading}
        </Typography>
        {options.length > 8 && (
          <Box sx={{ px: 1.25, pb: 1 }} onKeyDown={(e) => e.stopPropagation()}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            />
          </Box>
        )}
        <MenuItem onClick={() => pick('')} sx={itemSx}>
          {check(!value)}
          {allLabel}
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />
        {shown.map((o) => (
          <MenuItem key={o.value} onClick={() => pick(o.value)} sx={itemSx}>
            {check(value === o.value)}
            {o.label}
          </MenuItem>
        ))}
        {!shown.length && <Typography sx={{ px: 2, py: 1, fontSize: 12.5, color: T.ink3 }}>No match</Typography>}
      </Menu>
    </>
  );
};

/** The group's clinics as a filter dropdown. */
export const ClinicFilter: React.FC<{ value: string; onChange: (id: string) => void }> = ({ value, onChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    dispatch(getGroupAdminClinics())
      .unwrap()
      .then((clinics) => setOptions(clinics.map((c) => ({ value: c._id, label: c.name || '—' }))))
      .catch(() => setOptions([]));
  }, [dispatch]);

  return (
    <FilterMenu
      value={value}
      onChange={onChange}
      allLabel="All clinics"
      heading="Filter by clinic"
      icon={<LocalHospitalOutlinedIcon sx={{ fontSize: 15 }} />}
      options={options}
    />
  );
};

/** A labelled date input in the filter panel. */
export const DateField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <label className="csl-field">
    <span>{label}</span>
    <input type="date" value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);

/** Column header band above the rows; `columns` is a CSS grid template. */
export const HeadBand: React.FC<{ columns: string; minWidth: number; labels: string[] }> = ({ columns, minWidth, labels }) => (
  <Box sx={{ overflow: 'hidden', flexShrink: 0 }}>
    <Box aria-hidden sx={{ display: 'grid', gridTemplateColumns: columns, minWidth, alignItems: 'center', gap: '16px', mx: '4px', pl: '22px', pr: '18px', pb: '8px' }}>
      {labels.map((l, i) => (
        <Typography
          key={l || i}
          sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.ink3, textAlign: i === labels.length - 1 && !l ? 'right' : 'left' }}
        >
          {l}
        </Typography>
      ))}
    </Box>
  </Box>
);

/** One glass row card with a status stripe, laid out on `columns`. */
export const RowCard: React.FC<{
  columns: string;
  minWidth: number;
  stripe?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ columns, minWidth, stripe, onClick, children }) => (
  <Card
    onClick={onClick}
    sx={{
      position: 'relative',
      overflow: 'visible',
      flexShrink: 0,
      display: 'grid',
      gridTemplateColumns: columns,
      minWidth,
      alignItems: 'center',
      gap: '16px',
      p: '14px 18px 14px 22px',
      cursor: onClick ? 'pointer' : 'default',
      background: 'linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))',
      border: `1px solid ${T.line}`,
      borderRadius: '16px',
      backdropFilter: 'blur(7px)',
      transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease, background 0.16s ease',
      '&:hover': {
        transform: onClick ? 'translateY(-2px)' : 'none',
        borderColor: T.lineStrong,
        background: 'linear-gradient(180deg, rgba(30,50,76,0.78), rgba(18,34,54,0.75))',
        boxShadow: '0 16px 38px -18px rgba(0,0,0,0.85)',
      },
    }}
  >
    {stripe && (
      <Box aria-hidden sx={{ position: 'absolute', left: 0, top: 14, bottom: 14, width: 3, borderRadius: '0 3px 3px 0', background: stripe }} />
    )}
    {children}
  </Card>
);

/** Avatar + two lines (name, sub-line) — the identity cell of a row. */
export const Identity: React.FC<{ name: string; sub?: React.ReactNode; initialsFrom?: string | null }> = ({ name, sub, initialsFrom }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', minWidth: 0 }}>
    <Avatar
      variant="rounded"
      sx={{
        width: 42,
        height: 42,
        flexShrink: 0,
        fontSize: 14,
        fontWeight: 700,
        color: '#04221d',
        borderRadius: '12px',
        background: `linear-gradient(135deg, ${T.teal}, ${T.sky})`,
        boxShadow: '0 4px 14px -4px rgba(45,212,191,0.5)',
      }}
    >
      {initialsOf(initialsFrom ?? name)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
        {name}
      </Typography>
      {sub && (
        <Typography noWrap component="div" sx={{ fontSize: 12.5, color: T.ink3 }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

/** A small cell: optional caption over a value. */
export const Cell: React.FC<{ children: React.ReactNode; sub?: React.ReactNode; align?: 'right' }> = ({ children, sub, align }) => (
  <Box sx={{ minWidth: 0, textAlign: align }}>
    <Typography noWrap component="div" sx={{ fontSize: 13.5, color: T.ink }}>
      {children}
    </Typography>
    {sub && (
      <Typography noWrap component="div" sx={{ fontSize: 12, color: T.ink3 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

/** Loading pulse or the empty message inside the list. */
export const ListState: React.FC<{ loading: boolean; empty: boolean; title: string; hint: string; icon: string }> = ({ loading, empty, title, hint, icon }) => {
  if (loading && empty) {
    return (
      <div className="csl-empty">
        <svg className="csl-ekg" viewBox="0 0 150 46" fill="none" aria-hidden="true">
          <polyline className="csl-ekg__line" points="0,23 40,23 50,23 56,9 64,37 72,23 92,23 100,15 108,31 116,23 150,23" />
          <polyline className="csl-ekg__pulse" points="0,23 40,23 50,23 56,9 64,37 72,23 92,23 100,15 108,31 116,23 150,23" />
        </svg>
        Loading…
      </div>
    );
  }
  if (!empty) return null;
  return (
    <div className="csl-empty">
      <div className="csl-empty__icon">{icon}</div>
      <div className="csl-empty__t">{title}</div>
      <div className="csl-empty__s">{hint}</div>
    </div>
  );
};

const PER_PAGE = [10, 25, 50];

/** Pager in the list style: range, per-page, first/prev/pages/next/last. */
export const ListPager: React.FC<{
  pagination: Paging;
  onPage: (page: number) => void;
  onPerPage: (perPage: number) => void;
}> = ({ pagination, onPage, onPerPage }) => {
  const { page, pages, limit, total } = pagination;
  if (!total) return null;
  const first = (page - 1) * limit + 1;
  const last = Math.min(total, page * limit);
  const windowStart = Math.max(1, Math.min(page - 2, pages - 4));
  const window = Array.from({ length: Math.min(5, pages) }, (_, i) => windowStart + i);
  return (
    <div className="csl-pager">
      <div className="csl-pager__per">
        <span>
          {first}–{last} of {total.toLocaleString()}
        </span>
        <select value={limit} onChange={(e) => onPerPage(Number(e.target.value))}>
          {PER_PAGE.map((n) => (
            <option key={n} value={n}>
              {n}/page
            </option>
          ))}
        </select>
      </div>
      <div className="csl-pager__nav">
        <button disabled={page <= 1} onClick={() => onPage(1)}>« First</button>
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}>‹ Prev</button>
        {window.map((p) => (
          <button key={p} className={`crl-page ${p === page ? 'is-active' : ''}`} onClick={() => onPage(p)}>
            {p}
          </button>
        ))}
        <button disabled={page >= pages} onClick={() => onPage(page + 1)}>Next ›</button>
        <button disabled={page >= pages} onClick={() => onPage(pages)}>Last »</button>
      </div>
    </div>
  );
};

// ---- tables (dashboard, report view) -------------------------------------------

export const thSx = {
  textAlign: 'left' as const,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: T.ink3,
  p: '10px 12px',
  borderBottom: `1px solid ${T.line}`,
  whiteSpace: 'nowrap' as const,
};

export const tdSx = { fontSize: 13.5, color: T.ink, p: '11px 12px', borderBottom: `1px solid ${T.line}` };

export const tableCardSx = {
  background: 'linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))',
  border: `1px solid ${T.line}`,
  borderRadius: '16px',
  overflow: 'hidden',
};

// ---- clips ---------------------------------------------------------------------

const isVideo = (url?: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url || '');

/** Grid of converted JPEG/WebM clips. */
export const LiteImageGrid: React.FC<{ instances: LiteInstance[] }> = ({ instances }) =>
  instances.length ? (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
      {instances.map((inst, i) => (
        <Box key={inst.instanceUID || i} sx={{ background: T.well, border: `1px solid ${T.line}`, borderRadius: '12px', overflow: 'hidden' }}>
          {isVideo(inst.mediaUrl) ? (
            <video src={inst.mediaUrl} autoPlay loop muted playsInline style={{ width: '100%', display: 'block' }} />
          ) : (
            <img src={inst.mediaUrl} alt={inst.view || `Image ${i + 1}`} loading="lazy" style={{ width: '100%', display: 'block' }} />
          )}
          {(inst.view || inst.stage) && (
            <Typography sx={{ fontSize: 11.5, color: T.ink3, p: '6px 8px' }}>{[inst.stage, inst.view].filter(Boolean).join(' · ')}</Typography>
          )}
        </Box>
      ))}
    </Box>
  ) : (
    <Typography sx={{ fontSize: 13, color: T.ink3 }}>No converted images for this study yet.</Typography>
  );

/** Dialog that loads and shows a study's converted clips. */
export const LiteImagesDialog: React.FC<{
  open: boolean;
  title: string;
  load: () => Promise<LiteInstance[]>;
  onClose: () => void;
}> = ({ open, title, load, onClose }) => {
  const [instances, setInstances] = useState<LiteInstance[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setInstances(null);
    setError('');
    load()
      .then((list) => !cancelled && setInstances(list))
      .catch((e) => !cancelled && setError(typeof e === 'string' ? e : 'Could not load the images'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { background: T.surface, backgroundImage: 'none', border: `1px solid ${T.line}`, borderRadius: '16px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Typography sx={{ color: T.red, fontSize: 13 }}>{error}</Typography>
        ) : instances ? (
          <LiteImageGrid instances={instances} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
